import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { TargetAccount, Owner, SortField, SortOrder, TargetAccountsFilters, AccountStatus } from './types'
import { AVAILABLE_COLUMNS } from './types'
import { targetAccountsApi, preferencesApi } from '@api/client'

interface TargetAccountsState {
  // Data
  accounts: TargetAccount[]
  owners: Owner[]
  isLoading: boolean
  error: string | null

  // Selection
  selectedAccountIds: string[]

  // Filters
  filters: TargetAccountsFilters
  sortField: SortField
  sortOrder: SortOrder

  // Column visibility
  visibleColumns: string[]

  // Preferences sync
  preferencesLoaded: boolean

  // Actions
  loadAccounts: () => Promise<void>
  loadPreferences: (userId: string, userEmail?: string) => Promise<void>
  savePreferences: (userId: string, userEmail?: string) => Promise<void>
  setFilter: <K extends keyof TargetAccountsFilters>(key: K, value: TargetAccountsFilters[K]) => void
  clearFilters: () => void
  setSort: (field: SortField) => void
  toggleColumn: (columnId: string, userId?: string, userEmail?: string) => void
  resetColumns: (userId?: string, userEmail?: string) => void

  // Selection actions
  selectAccount: (id: string) => void
  selectAllAccounts: (ids: string[]) => void
  clearSelection: () => void

  // Bulk actions
  bulkSetStatus: (status: AccountStatus) => void
  bulkSetOwner: (ownerId: string) => void
}

const defaultFilters: TargetAccountsFilters = {
  search: '',
  owner: 'all',
  sector: 'all',
  overallScore: 'all',
  status: 'all',
}

// Get default visible columns
const defaultVisibleColumns = AVAILABLE_COLUMNS
  .filter((col) => col.defaultVisible)
  .map((col) => col.id)

// Debounce helper for saving preferences
let saveTimeout: ReturnType<typeof setTimeout> | null = null
const debouncedSave = (fn: () => void, delay: number = 1000) => {
  if (saveTimeout) clearTimeout(saveTimeout)
  saveTimeout = setTimeout(fn, delay)
}

export const useTargetAccountsStore = create<TargetAccountsState>()(
  persist(
    (set, get) => ({
      // Initial state
      accounts: [],
      owners: [],
      isLoading: false,
      error: null,
      selectedAccountIds: [],
      filters: defaultFilters,
      sortField: 'overall_icp_score',
      sortOrder: 'desc',
      visibleColumns: defaultVisibleColumns,
      preferencesLoaded: false,

      // Load accounts from API
      loadAccounts: async () => {
        set({ isLoading: true, error: null })

        const response = await targetAccountsApi.list()

        if (response.success && response.data) {
          set({
            accounts: response.data.accounts,
            owners: response.data.owners,
            isLoading: false,
          })
        } else {
          set({
            error: response.error || 'Failed to load accounts',
            isLoading: false,
          })
        }
      },

      // Load preferences from API
      loadPreferences: async (userId: string, userEmail?: string) => {
        try {
          const response = await preferencesApi.get(userId)
          if (response.success && response.data?.preferences) {
            const prefs = response.data.preferences
            set({
              visibleColumns: prefs.target_accounts_columns || defaultVisibleColumns,
              sortField: (prefs.target_accounts_sort_field as SortField) || 'overall_icp_score',
              sortOrder: (prefs.target_accounts_sort_order as SortOrder) || 'desc',
              preferencesLoaded: true,
            })
          } else {
            // No preferences yet, create defaults
            set({ preferencesLoaded: true })
            // Save defaults to API
            await preferencesApi.save({
              user_id: userId,
              user_email: userEmail,
              target_accounts_columns: defaultVisibleColumns,
              target_accounts_sort_field: 'overall_icp_score',
              target_accounts_sort_order: 'desc',
            })
          }
        } catch (error) {
          console.error('Failed to load preferences:', error)
          set({ preferencesLoaded: true })
        }
      },

      // Save preferences to API
      savePreferences: async (userId: string, userEmail?: string) => {
        const { visibleColumns, sortField, sortOrder } = get()
        try {
          await preferencesApi.save({
            user_id: userId,
            user_email: userEmail,
            target_accounts_columns: visibleColumns,
            target_accounts_sort_field: sortField,
            target_accounts_sort_order: sortOrder,
          })
        } catch (error) {
          console.error('Failed to save preferences:', error)
        }
      },

      // Set individual filter
      setFilter: (key, value) => {
        set((state) => ({
          filters: { ...state.filters, [key]: value },
        }))
      },

      // Clear all filters
      clearFilters: () => {
        set({ filters: defaultFilters })
      },

      // Toggle sort
      setSort: (field) => {
        const { sortField, sortOrder } = get()
        if (sortField === field) {
          set({ sortOrder: sortOrder === 'asc' ? 'desc' : 'asc' })
        } else {
          set({ sortField: field, sortOrder: 'desc' })
        }
      },

      // Toggle column visibility
      toggleColumn: (columnId, userId, userEmail) => {
        const { visibleColumns, savePreferences } = get()
        if (visibleColumns.includes(columnId)) {
          // Don't allow hiding the company name column
          if (columnId === 'name') return
          set({ visibleColumns: visibleColumns.filter((id) => id !== columnId) })
        } else {
          set({ visibleColumns: [...visibleColumns, columnId] })
        }
        // Save to API with debounce if userId provided
        if (userId) {
          debouncedSave(() => savePreferences(userId, userEmail))
        }
      },

      // Reset to default columns
      resetColumns: (userId, userEmail) => {
        const { savePreferences } = get()
        set({ visibleColumns: defaultVisibleColumns })
        // Save to API if userId provided
        if (userId) {
          debouncedSave(() => savePreferences(userId, userEmail))
        }
      },

      // Selection actions
      selectAccount: (id) => {
        const { selectedAccountIds } = get()
        if (selectedAccountIds.includes(id)) {
          set({ selectedAccountIds: selectedAccountIds.filter((sid) => sid !== id) })
        } else {
          set({ selectedAccountIds: [...selectedAccountIds, id] })
        }
      },

      selectAllAccounts: (ids) => {
        const { selectedAccountIds } = get()
        const allSelected = ids.every((id) => selectedAccountIds.includes(id))
        if (allSelected) {
          // Deselect all visible
          set({ selectedAccountIds: selectedAccountIds.filter((id) => !ids.includes(id)) })
        } else {
          // Select all visible (merge with existing)
          const merged = [...new Set([...selectedAccountIds, ...ids])]
          set({ selectedAccountIds: merged })
        }
      },

      clearSelection: () => {
        set({ selectedAccountIds: [] })
      },

      // Bulk actions
      bulkSetStatus: (status) => {
        const { selectedAccountIds, accounts } = get()
        const updated = accounts.map((account) =>
          selectedAccountIds.includes(account.id)
            ? { ...account, account_status: status }
            : account
        )
        set({ accounts: updated, selectedAccountIds: [] })
      },

      bulkSetOwner: (ownerId) => {
        const { selectedAccountIds, accounts } = get()
        const updated = accounts.map((account) =>
          selectedAccountIds.includes(account.id)
            ? { ...account, hubspot_owner_id: ownerId }
            : account
        )
        set({ accounts: updated, selectedAccountIds: [] })
      },
    }),
    {
      name: 'target-accounts-storage',
      partialize: (state) => ({
        filters: state.filters,
        sortField: state.sortField,
        sortOrder: state.sortOrder,
        visibleColumns: state.visibleColumns,
      }),
    }
  )
)
